import { z } from "zod";
import { NOTES_MAX, CONTACT_NAME_MAX } from "@shared/constants/validation.js";
import { CAREGIVER_EMAIL_MAX } from "./sharedLink.schema.js";

/** Optional, self-reported identity: empty strings collapse to null. */
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullish()
    .transform((v) => (v && v.length > 0 ? v : null));

/** `beta_feedback.page_path`: the CHECK in migration 0027 allows 1–200 characters. */
export const PAGE_PATH_MAX = 200;

/**
 * A site path: one leading "/", never "//" or "/\" (a protocol-relative URL is
 * a host, not a page), and no control characters. Query strings and fragments
 * pass here and are dropped by the server (lib/pagePath.ts), which also masks
 * tokens and record ids before anything is stored.
 */
function isSitePath(value: string): boolean {
  if (!value.startsWith("/") || value.startsWith("//") || value.startsWith("/\\")) return false;
  for (const ch of value) {
    const code = ch.charCodeAt(0);
    if (code <= 0x1f || code === 0x7f) return false;
  }
  return true;
}

export const createBetaFeedbackSchema = z.object({
  name: optionalText(CONTACT_NAME_MAX),
  // Optional and trimmed. The blank -> null transform runs BEFORE the address
  // check (hence refine, not .email()) — an untouched input posts "", which
  // .email() would otherwise reject on a field the user chose to skip.
  email: z
    .string()
    .trim()
    .max(CAREGIVER_EMAIL_MAX, "Email is too long")
    .nullable()
    .optional()
    .transform((v) => (v && v.length > 0 ? v : null))
    .refine((v) => v === null || z.string().email().safeParse(v).success, {
      message: "Enter a valid email address",
    }),
  rating: z
    .number()
    .int("Rating must be a whole number")
    .min(1, "Rating must be between 1 and 5")
    .max(5, "Rating must be between 1 and 5")
    .nullish()
    .transform((v) => v ?? null),
  feedback: z
    .string()
    .trim()
    .min(1, "Please tell us what you think")
    .max(NOTES_MAX, "Feedback is too long"),
  /**
   * The page a signed-in tester sent the note from (the help menu sends
   * `location.pathname`; the landing form sends nothing). Optional, trimmed,
   * blank -> null, a path only (see isSitePath).
   */
  pagePath: z
    .string()
    .trim()
    .max(PAGE_PATH_MAX, "Page path is too long")
    .nullish()
    .transform((v) => (v && v.length > 0 ? v : null))
    .refine((v) => v === null || isSitePath(v), {
      message: "Page must be a path on this site, like /dashboard",
    }),
});

export type CreateBetaFeedbackInput = z.infer<typeof createBetaFeedbackSchema>;
/** The request body (before parsing): every field but `feedback` may be left out. */
export type CreateBetaFeedbackBody = z.input<typeof createBetaFeedbackSchema>;
